import { Modal as BootstrapModal } from 'bootstrap'
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ModalSize = 'md' | 'lg' | 'fullscreen'

interface ModalProps {
  show: boolean
  onClose: () => void
  title?: string
  size?: ModalSize
  children: ReactNode
  footer?: ReactNode
}

const SIZE_CLASS: Record<ModalSize, string> = {
  md: '',
  lg: 'modal-lg',
  fullscreen: 'modal-fullscreen modal-fullscreen-sm-down',
}

/**
 * Ref-driven wrapper around Bootstrap's own Modal JS (same pattern as
 * AppSidebar's SimpleBar wiring) rather than the declarative data-bs-*
 * attribute API, since this needs to be opened/closed from arbitrary React
 * state instead of a fixed DOM trigger button.
 */
export function Modal({ show, onClose, title, size = 'md', children, footer }: ModalProps) {
  const elementRef = useRef<HTMLDivElement>(null)
  const instanceRef = useRef<BootstrapModal | null>(null)

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    const instance = new BootstrapModal(el, { backdrop: true, keyboard: true })
    instanceRef.current = instance

    const handleHidden = () => onClose()
    el.addEventListener('hidden.bs.modal', handleHidden)

    return () => {
      el.removeEventListener('hidden.bs.modal', handleHidden)
      instance.dispose()
      instanceRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const instance = instanceRef.current
    if (!instance) return
    if (show) {
      instance.show()
    } else {
      instance.hide()
    }
  }, [show])

  return createPortal(
    <div className="modal fade" ref={elementRef} tabIndex={-1} aria-hidden={!show}>
      <div className={`modal-dialog modal-dialog-centered ${SIZE_CLASS[size]}`.trim()}>
        <div className="modal-content">
          {title && (
            <div className="modal-header">
              <h6 className="modal-title">{title}</h6>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
          )}
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  )
}
